// domains/PostCreator/utils/stageConfig.ts
import { PostStatus, VALID_STATUSES } from "@/core/types/postTypes";

// Each stage maps to exactly one PostStatus (1:1 mapping)
export type PostStage =
  | "all"
  | "ai_generated"
  | "draft"
  | "caption_generated"
  | "approval_pending"
  | "ready"
  | "scheduled"
  | "partially_published"
  | "published"
  | "archived";

export const getStatisticsOrder = (): PostStatus[] => [
  PostStatus.AI_GENERATED,
  PostStatus.DRAFT,
  PostStatus.CAPTION_GENERATED,
  PostStatus.APPROVAL_PENDING,
  PostStatus.READY,
  PostStatus.SCHEDULED,
  PostStatus.PARTIALLY_PUBLISHED,
  PostStatus.PUBLISHED,
  PostStatus.ARCHIVED,
];

export interface StageConfig {
  id: PostStage;
  label: string;
  description: string;
  statuses: PostStatus[];
  color: string;
  bgColor: string;
  textColor: string;
  icon: string;
}

// One tab per status - no grouping
export const STAGE_CONFIGS: StageConfig[] = [
  {
    id: "ai_generated",
    label: "AI",
    description: "Post generati da AI",
    statuses: [PostStatus.AI_GENERATED],
    color: "violet",
    bgColor: "bg-violet-50",
    textColor: "text-violet-700",
    icon: "🤖",
  },
  {
    id: "draft",
    label: "Bozza",
    description: "Post in fase di stesura",
    statuses: [PostStatus.DRAFT],
    color: "slate",
    bgColor: "bg-slate-50",
    textColor: "text-slate-700",
    icon: "📝",
  },
  {
    id: "caption_generated",
    label: "Con didascalia",
    description: "Post con didascalia generata",
    statuses: [PostStatus.CAPTION_GENERATED],
    color: "blue",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    icon: "💬",
  },
  {
    id: "approval_pending",
    label: "In attesa",
    description: "Post in attesa di approvazione del cliente",
    statuses: [PostStatus.APPROVAL_PENDING],
    color: "amber",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    icon: "⏳",
  },
  {
    id: "ready",
    label: "Pronto",
    description: "Post pronti per la programmazione",
    statuses: [PostStatus.READY],
    color: "teal",
    bgColor: "bg-teal-50",
    textColor: "text-teal-700",
    icon: "✅",
  },
  {
    id: "scheduled",
    label: "Programmato",
    description: "Post programmati per la pubblicazione",
    statuses: [PostStatus.SCHEDULED],
    color: "purple",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    icon: "📅",
  },
  {
    id: "partially_published",
    label: "Parziale",
    description: "Post pubblicati parzialmente (alcune piattaforme non hanno risposto)",
    statuses: [PostStatus.PARTIALLY_PUBLISHED],
    color: "orange",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    icon: "⚠️",
  },
  {
    id: "published",
    label: "Pubblicato",
    description: "Post pubblicati ovunque",
    statuses: [PostStatus.PUBLISHED],
    color: "emerald",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    icon: "🎉",
  },
  {
    id: "archived",
    label: "Archiviato",
    description: "Post archiviati",
    statuses: [PostStatus.ARCHIVED],
    color: "gray",
    bgColor: "bg-gray-50",
    textColor: "text-gray-700",
    icon: "🗑️",
  },
  {
    id: "all",
    label: "Tutti",
    description: "Tutti i post",
    statuses: [...VALID_STATUSES] as PostStatus[],
    color: "gray",
    bgColor: "bg-gray-50",
    textColor: "text-gray-700",
    icon: "📋",
  },
];

export const getStageByStatus = (status: PostStatus): PostStage => {
  const stage = STAGE_CONFIGS.find((config) =>
    config.statuses.includes(status),
  );
  return stage?.id || "ai_generated";
};

export const getStageConfig = (stage: PostStage): StageConfig => {
  return (
    STAGE_CONFIGS.find((config) => config.id === stage) || STAGE_CONFIGS[0]
  );
};

export const getStageStatistics = (posts: any[], stage: PostStage) => {
  const stageConfig = getStageConfig(stage);
  const stagePosts = posts.filter((post) =>
    stageConfig.statuses.includes(post.status),
  );

  return {
    total: stagePosts.length,
    byStatus: stageConfig.statuses.reduce(
      (acc, status) => {
        acc[status] = posts.filter((post) => post.status === status).length;
        return acc;
      },
      {} as Record<string, number>,
    ),
  };
};
